/**
 * Supabase/Database compatibility layer for Spire Dev
 * Provides types and utilities for property data
 */

// Property type definition (compatible with both Supabase and local PostgreSQL)
export interface Property {
  id: string
  title: string
  slug: string
  description: string | null
  property_type: string
  propertyType?: string
  listing_type: string
  status: string
  price: number
  currency?: string
  address: string | null
  city: string | null
  state?: string | null
  bedrooms: number | null
  bathrooms: number | null
  floor_area: number | null
  floorAreaSqm?: number | null
  lot_area: number | null
  parking?: number | null
  parkingSpaces?: number | null
  furnishing?: string | null
  featured_image_url?: string | null
  images_urls?: string[]
  images?: string[]
  project_name?: string | null
  projectName?: string | null
  is_featured?: boolean
  created_at: string
  updated_at: string
}

// Format price with PHP currency (FULL FORMAT with commas - matches /lib/formatters.ts)
export function formatPrice(price: number, currency: string = 'PHP'): string {
  if (!price || isNaN(price)) return '₱0'

  const symbol = currency === 'PHP' ? '₱' : '$'

  // Format with commas: 70000000 -> ₱70,000,000
  const formatted = Math.round(price).toLocaleString('en-PH')
  return `${symbol}${formatted}`
}

// Alias for backward compatibility
export function formatPriceFull(price: number, currency: string = 'PHP'): string {
  return formatPrice(price, currency)
}

// Format display location (city, state)
export function formatDisplayLocation(property: Property): string {
  const parts: string[] = []

  if (property.city) {
    parts.push(property.city)
  }

  if (property.state && property.state !== property.city) {
    parts.push(property.state)
  }

  return parts.join(', ') || 'Metro Manila'
}

// Fetch featured properties from local database or API
export async function getFeaturedProperties(limit: number = 8): Promise<Property[]> {
  try {
    // Fetch from local API endpoint
    const response = await fetch(`http://localhost:3003/api/public/properties?limit=${limit}&status=active`, {
      cache: 'no-store'
    })

    if (!response.ok) {
      console.error('Failed to fetch featured properties:', response.statusText)
      return []
    }

    const data = await response.json()
    return (data.data?.properties || []) as Property[]
  } catch (error) {
    console.error('Error fetching featured properties:', error)
    return []
  }
}

// Fetch single property by slug
export async function getPropertyBySlug(slug: string): Promise<Property | null> {
  try {
    const response = await fetch(`/api/properties/${slug}`, {
      cache: 'no-store'
    })

    if (!response.ok) {
      console.error('Failed to fetch property:', response.statusText)
      return null
    }

    const data = await response.json()
    // API returns {success: true, data: {property}}
    return (data.data || data.property || null) as Property | null
  } catch (error) {
    console.error('Error fetching property by slug:', error)
    return null
  }
}

// Fetch properties with filters
export async function getProperties(params: {
  listingType?: string
  propertyType?: string
  city?: string
  minPrice?: number
  maxPrice?: number
  bedrooms?: number
  page?: number
  limit?: number
  sortBy?: string
}): Promise<{ properties: Property[], total: number }> {
  try {
    const queryParams = new URLSearchParams()

    if (params.listingType) queryParams.append('listing_type', params.listingType)
    if (params.propertyType) queryParams.append('property_type', params.propertyType)
    if (params.city) queryParams.append('city', params.city)
    if (params.minPrice) queryParams.append('min_price', params.minPrice.toString())
    if (params.maxPrice) queryParams.append('max_price', params.maxPrice.toString())
    if (params.bedrooms) queryParams.append('bedrooms', params.bedrooms.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.page) queryParams.append('offset', ((params.page - 1) * (params.limit || 50)).toString())

    const response = await fetch(`http://localhost:3003/api/public/properties?${queryParams}`, {
      cache: 'no-store'
    })

    if (!response.ok) {
      console.error('Failed to fetch properties:', response.statusText)
      return { properties: [], total: 0 }
    }

    const data = await response.json()
    return {
      properties: (data.data?.properties || []) as Property[],
      total: data.data?.total || 0
    }
  } catch (error) {
    console.error('Error fetching properties:', error)
    return { properties: [], total: 0 }
  }
}
